import { useEffect, useRef } from 'react';

import { Animated, Easing, Platform, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import CloseButton from 'components/close-button';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

/**
 * Which side of the bubble the arrow sits on, and therefore where the bubble sits
 * relative to its anchor:
 * - `top`    — bubble below the anchor, arrow points up
 * - `bottom` — bubble above the anchor, arrow points down
 * - `left`   — bubble right of the anchor, arrow points left
 * - `right`  — bubble left of the anchor, arrow points right
 */
export type CoachmarkPlacement = 'top' | 'bottom' | 'left' | 'right';

/** Where the arrow sits along its edge. */
export type CoachmarkArrowAlign = 'start' | 'center' | 'end';

export type CoachmarkProps = {
  /** Resolved heading text. Callers own translation. */
  title: string;
  /** Resolved supporting text. Callers own translation. */
  body: string;
  onDismiss: () => void;
  /** Arrow side / bubble position relative to the anchor. Defaults to `bottom`. */
  placement?: CoachmarkPlacement;
  /** Arrow position along its edge. Defaults to `center`. */
  arrowAlign?: CoachmarkArrowAlign;
  /**
   * Positioning belongs to the caller: the coachmark is absolutely positioned against
   * whichever element it points at, so anchoring is layout the caller controls. `placement`
   * sets a sensible default anchor (e.g. `bottom` → `bottom: '100%'`); this style is merged
   * on top so a caller can offset or override it.
   */
  style?: StyleProp<ViewStyle>;
  /** Root testID; the dismiss control derives from it as `${testID}-dismiss`. */
  testID?: string;
};

const POINTER_SIZE = 8;
const POINTER_BASE = POINTER_SIZE + 2;
const CLOSE_BUTTON_SIZE = 28;
const FADE_IN_DURATION_MS = 300;
const FADE_IN_TRANSLATE = 12;

const isVertical = (placement: CoachmarkPlacement) => placement === 'top' || placement === 'bottom';

/** Anchors the container to the opposite side from where the arrow points. */
const containerPlacementStyle: Record<CoachmarkPlacement, ViewStyle> = {
  top: { top: '100%', left: spacing.$1, right: spacing.$1, marginTop: spacing.$4 },
  bottom: {
    // react-native-web resolves `bottom: '100%'` against the anchor's own height, which
    // pushes the bubble too far up once the anchor is `position: relative` (see the
    // coachmarkAnchor fix in store-locator-with-map). 75% matches the intended overlap on web;
    // native Yoga layout doesn't have this issue, so it keeps the full 100%. The web offset
    // already accounts for the gap, so marginBottom is zeroed there to avoid double-spacing.
    bottom: Platform.OS === 'web' ? '75%' : '100%',
    left: spacing.$1,
    right: spacing.$1,
    marginBottom: Platform.OS === 'web' ? 0 : spacing.$4,
  },
  left: { left: '100%', marginLeft: spacing.$4 },
  right: { right: '100%', marginRight: spacing.$4 },
};

/** Stacks the bubble and arrow along the placement axis, arrow on the anchor-facing side. */
const containerFlexStyle: Record<CoachmarkPlacement, ViewStyle> = {
  top: { flexDirection: 'column-reverse', alignItems: 'stretch' },
  bottom: { flexDirection: 'column', alignItems: 'stretch' },
  left: { flexDirection: 'row-reverse', alignItems: 'center' },
  right: { flexDirection: 'row', alignItems: 'center' },
};

/** The triangle: a border box with one coloured side pointing toward the anchor. */
const pointerPlacementStyle: Record<CoachmarkPlacement, ViewStyle> = {
  top: {
    borderLeftWidth: POINTER_BASE,
    borderRightWidth: POINTER_BASE,
    borderBottomWidth: POINTER_SIZE,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: primitive.$black,
  },
  bottom: {
    borderLeftWidth: POINTER_BASE,
    borderRightWidth: POINTER_BASE,
    borderTopWidth: POINTER_SIZE,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: primitive.$black,
  },
  left: {
    borderTopWidth: POINTER_BASE,
    borderBottomWidth: POINTER_BASE,
    borderRightWidth: POINTER_SIZE,
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent',
    borderRightColor: primitive.$black,
  },
  right: {
    borderTopWidth: POINTER_BASE,
    borderBottomWidth: POINTER_BASE,
    borderLeftWidth: POINTER_SIZE,
    borderTopColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: primitive.$black,
  },
};

/** Pulls the bubble a pixel toward the arrow to close the Android sub-pixel seam. */
const bubbleSeamOverlapStyle: Record<CoachmarkPlacement, ViewStyle> = {
  top: { marginTop: -1 },
  bottom: { marginBottom: -1 },
  left: { marginLeft: -1 },
  right: { marginRight: -1 },
};

const arrowAlignToFlex: Record<CoachmarkArrowAlign, ViewStyle['alignSelf']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

/**
 * Non-blocking coachmark bubble with a directional pointer and a dismiss control.
 *
 * Presentation only — it holds no eligibility, analytics, or session state; the caller
 * decides when to render it and what dismiss does. Deliberately colocated with checkout
 * rather than promoted to `src/components`: checkout is its only consumer. Promote it once
 * a second feature needs it.
 *
 * `placement` chooses which side the arrow points and where the bubble sits relative to its
 * anchor; `arrowAlign` slides the arrow along that edge. Only the X captures presses; the
 * bubble is `box-none` and the text/pointer are `none`, so controls underneath stay usable.
 */
export const Coachmark = ({
  title,
  body,
  onDismiss,
  placement = 'bottom',
  arrowAlign = 'center',
  style,
  testID = 'coachmark',
}: CoachmarkProps) => {
  const coachmarkFadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const fadeInAnimation = Animated.timing(coachmarkFadeAnim, {
      toValue: 1,
      duration: FADE_IN_DURATION_MS,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    });

    fadeInAnimation.start();

    return () => {
      fadeInAnimation.stop();
    };
  }, [coachmarkFadeAnim]);

  // Slide the whole coachmark (bubble + pointer) into place along the placement axis, from
  // the anchor-facing side, so entry direction matches where it points.
  const vertical = isVertical(placement);
  const enterFrom = placement === 'bottom' || placement === 'right' ? -1 : 1;
  const slide = coachmarkFadeAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [enterFrom * FADE_IN_TRANSLATE, 0],
  });
  const transform = vertical ? [{ translateY: slide }] : [{ translateX: slide }];

  // The arrow aligns across the axis perpendicular to the bubble stack: for top/bottom it
  // slides horizontally (alignSelf), for left/right it slides vertically (same prop, the
  // cross axis differs because the container flips to a row).
  const pointerAlignStyle: ViewStyle = { alignSelf: arrowAlignToFlex[arrowAlign] };

  return (
    <Animated.View
      style={[
        styles.coachmarkContainer,
        containerFlexStyle[placement],
        containerPlacementStyle[placement],
        style,
        { opacity: coachmarkFadeAnim, transform },
      ]}
      pointerEvents="box-none"
      testID={testID}
    >
      <View
        style={[styles.coachmarkBubble, bubbleSeamOverlapStyle[placement]]}
        pointerEvents="box-none"
      >
        {/* Title and X share a row so the X tracks the title's baseline instead of a
            hardcoded offset that drifts whenever the bubble's padding changes. */}
        <View style={styles.coachmarkTitleRow} pointerEvents="box-none">
          <Typography style={styles.title} pointerEvents="none">
            {title}
          </Typography>
          <CloseButton
            onPress={onDismiss}
            skipRotateAnimation
            inverted
            noBackground
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            _svg={{ size: '$6', color: primitive.$white }}
            width={CLOSE_BUTTON_SIZE}
            height={CLOSE_BUTTON_SIZE}
            marginRight={spacing.$1}
            style={styles.coachmarkCloseButton}
            accessibilityLabel={t('close')}
            testID={`${testID}-dismiss`}
          />
        </View>
        <Typography style={styles.body} pointerEvents="none">
          {body}
        </Typography>
      </View>
      <View
        style={[styles.coachmarkPointerBase, pointerPlacementStyle[placement], pointerAlignStyle]}
        pointerEvents="none"
      />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  coachmarkContainer: {
    position: 'absolute',
    zIndex: 10,
    elevation: 10,
  },
  coachmarkPointerBase: {
    width: 0,
    height: 0,
    borderStyle: 'solid',
  },
  // padding: 0.4rem 0.75rem — $1_5 (6px) is the token nearest 0.4rem/6.4px.
  coachmarkBubble: {
    backgroundColor: primitive.$black,
    borderRadius: 8,
    paddingHorizontal: spacing.$3,
    paddingTop: spacing.$0_5,
    paddingBottom: spacing.$1_5,
  },
  coachmarkTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.$2,
  },
  // Headings-Button/md
  title: {
    flexShrink: 1,
    color: primitive.$white,
    fontSize: 16,
    lineHeight: 18,
    fontWeight: '400',
    fontFamily: primitive.fontFamily.$headers,
  },
  // Text-Body/xs. Line height is tightened from the token's 150% (18px) so the bubble
  // stays compact and the body sits closer under the title — the leading between the
  // two line boxes is what reads as the gap, since the rows have no flex gap.
  body: {
    color: primitive.$white,
    fontSize: 12,
    lineHeight: 15,
    fontWeight: '400',
    fontFamily: primitive.fontFamily.$body,
  },
  // width/height — overrides CloseButton's default $9 (36px) box.
  coachmarkCloseButton: {
    flexShrink: 0,
    width: CLOSE_BUTTON_SIZE,
    height: CLOSE_BUTTON_SIZE,
    marginRight: spacing.$1,
  },
});
